import { useState } from 'react'

import {
  InputLabel,
  Select,
  Space,
  TextButton,
  WithFormControlSelect,
} from '@procurify/ui'
import { useFormikContext } from 'formik'

import { PriceFormField } from '@webapp/components/PriceField'
import { useFeatureFlag, useProcurifyIntl } from '@webapp/hooks'
import { type Enum } from '@webapp/infrastructure/types/Enum'
import { TaxSelect } from '@webapp/platform/finance'
import { ExpenseTaxSelect } from '../../ExpenseTaxSelect'
import { isCustomTax } from '../../ExpenseTaxSelect'
import { type ExpenseLineItemFormSchema } from '../ExpenseLineItemForm'
import messages from '../messages'

const TaxFieldsModes = {
  PRESET: 'preset',
  CUSTOM: 'custom',
} as const
type TaxFieldsMode = Enum<typeof TaxFieldsModes>

export const CUSTOM_TAX_TYPE_OPTIONS = [
  { id: '%', name: '%' },
  { id: '$', name: '$' },
]

interface TaxFieldsProps {
  isSubmitting: boolean
}

export const TaxFields = ({ isSubmitting }: TaxFieldsProps) => {
  const intl = useProcurifyIntl()
  const formik = useFormikContext<ExpenseLineItemFormSchema>()

  const PAY_3067_ENHANCE_MILEAGE_EXPENSE_ITEMS = useFeatureFlag(
    'PAY_3067_ENHANCE_MILEAGE_EXPENSE_ITEMS'
  )

  const isCustomTaxSet =
    formik.values.taxAmount !== 0 &&
    (formik.values.taxType?.id === '%' || formik.values.taxType?.id === '$')
  const [activeMode, setActiveMode] = useState<TaxFieldsMode>(
    isCustomTaxSet ? TaxFieldsModes.CUSTOM : TaxFieldsModes.PRESET
  )

  /**
   * To be removed with PAY_3067_ENHANCE_MILEAGE_EXPENSE_ITEMS
   */
  const isTaxPresetSelected = isCustomTax(formik.values.taxType)

  return PAY_3067_ENHANCE_MILEAGE_EXPENSE_ITEMS ? (
    <>
      {activeMode === TaxFieldsModes.PRESET && (
        <>
          <Space align='center' direction='horizontal' justify='end'>
            <InputLabel>{intl.formatMessage(messages.taxLabel)}</InputLabel>
            <Space.Item width={2 / 3}>
              <TaxSelect
                aria-label={intl.formatMessage(messages.taxTypeAriaLabel)}
                inputId='taxType-select'
                value={
                  typeof formik.values.taxType?.id === 'number'
                    ? formik.values.taxType
                    : null
                }
                onChange={(opt) => formik.setFieldValue('taxType', opt)}
                isDisabled={isSubmitting}
                menuPortalTarget={document.body}
                menuPlacement='auto'
                placeholder={intl.formatMessage(
                  messages.taxSelectPlaceholderText
                )}
              />
            </Space.Item>
          </Space>
          <Space direction='horizontal' justify='end'>
            <TextButton
              type='button'
              onClick={() => {
                formik.setFieldValue('taxType', { id: '%', name: '%' })
                setActiveMode(TaxFieldsModes.CUSTOM)
              }}
            >
              {intl.formatMessage(messages.enterCustomTaxButtonLabel)}
            </TextButton>
          </Space>
        </>
      )}
      {activeMode === TaxFieldsModes.CUSTOM && (
        <>
          <Space align='center' direction='horizontal' justify='end'>
            <InputLabel>{intl.formatMessage(messages.taxLabel)}</InputLabel>
            <Space.Item width={1 / 3}>
              <PriceFormField
                id='taxAmount'
                name='taxAmount'
                aria-label={intl.formatMessage(messages.taxAmountLabel)}
                disabled={isSubmitting}
                inputProps={{ style: { textAlign: 'right' } }}
              />
            </Space.Item>
            <Space.Item width={1 / 3}>
              <Select
                aria-label={intl.formatMessage(messages.taxTypeAriaLabel)}
                inputId='taxType-select'
                options={CUSTOM_TAX_TYPE_OPTIONS}
                value={
                  typeof formik.values.taxType?.id === 'number'
                    ? CUSTOM_TAX_TYPE_OPTIONS[0]
                    : formik.values.taxType
                }
                onChange={(opt) => formik.setFieldValue('taxType', opt)}
                getOptionLabel={(opt) => opt.name}
                getOptionValue={(opt) => opt.id.toString()}
                isDisabled={isSubmitting}
                menuPortalTarget={document.body}
                menuPlacement='auto'
              />
            </Space.Item>
          </Space>
          <Space direction='horizontal' justify='end'>
            <TextButton
              type='button'
              onClick={() => {
                formik.setFieldValue('taxAmount', 0)
                setActiveMode(TaxFieldsModes.PRESET)
              }}
            >
              {intl.formatMessage(messages.useTaxPresetButtonText)}
            </TextButton>
          </Space>
        </>
      )}
    </>
  ) : (
    <Space align='center' direction='horizontal' justify='end'>
      <InputLabel htmlFor='taxAmount'>
        {intl.formatMessage(messages.taxAmountLabel)}
      </InputLabel>
      <Space.Item width={1 / 3}>
        <PriceFormField
          id='taxAmount'
          name='taxAmount'
          disabled={isSubmitting || isTaxPresetSelected}
          inputProps={{ style: { textAlign: 'right' } }}
        />
      </Space.Item>
      <Space.Item width={1 / 3}>
        <WithFormControlSelect id='taxType' name='taxType'>
          <ExpenseTaxSelect
            aria-label={intl.formatMessage(messages.taxTypeAriaLabel)}
            inputId='taxType-select'
            value={formik.values.taxType}
            onChange={(opt) => formik.setFieldValue('taxType', opt)}
            isDisabled={isSubmitting}
            menuPortalTarget={document.body}
            menuPlacement='auto'
          />
        </WithFormControlSelect>
      </Space.Item>
    </Space>
  )
}
